function getCaseInsensitiveSuggestionMatch(
  currentValue: string,
  suggestedPrompt: string,
): { suggestion: string; suffix: string } | null {
  const suggestion = suggestedPrompt.trim();
  if (!suggestion) return null;

  if (currentValue === "") {
    return { suggestion, suffix: suggestion };
  }

  const lowerCurrent = currentValue.toLowerCase();
  const lowerSuggestion = suggestion.toLowerCase();
  // Fully equal (case-insensitive) → no remaining suggestion.
  if (lowerCurrent === lowerSuggestion) return null;
  if (!lowerSuggestion.startsWith(lowerCurrent)) return null;

  return {
    suggestion,
    suffix: suggestion.slice(currentValue.length),
  };
}

/** Remaining ghost-text suffix when the typed value is a prefix of the suggestion. */
export function getInlineSuggestionSuffix(
  currentValue: string,
  suggestedPrompt: string,
): string | null {
  // Empty field uses the placeholder instead of inline ghost text.
  if (!currentValue) return null;
  const match = getCaseInsensitiveSuggestionMatch(currentValue, suggestedPrompt);
  if (!match || !match.suffix) return null;
  return match.suffix;
}

/** True when empty or a case-insensitive prefix of the suggestion (and not already equal). */
export function isSuggestionPrefixMatch(
  currentValue: string,
  suggestedPrompt: string,
): boolean {
  return getCaseInsensitiveSuggestionMatch(currentValue, suggestedPrompt) !== null;
}

/**
 * Non-empty typed prefix of the suggestion (case-insensitive).
 * Used for Tab hint + ghost remainder (not for empty / native placeholder).
 */
export function isNonEmptySuggestionPrefixMatch(
  currentValue: string,
  suggestedPrompt: string,
): boolean {
  return (
    currentValue.length > 0 &&
    isSuggestionPrefixMatch(currentValue, suggestedPrompt)
  );
}

/** Remaining suggestion text after a non-empty matching prefix; otherwise null. */
export function getSuggestionGhostSuffix(
  currentValue: string,
  suggestedPrompt: string,
): string | null {
  if (!currentValue) return null;
  const match = getCaseInsensitiveSuggestionMatch(
    currentValue,
    suggestedPrompt,
  );
  if (!match) return null;
  const suffix = match.suggestion.slice(currentValue.length);
  return suffix.length > 0 ? suffix : null;
}

export function resolveTabCompletedPrompt(
  currentValue: string,
  suggestedPrompt: string,
  maxLength: number,
): string | null {
  const match = getCaseInsensitiveSuggestionMatch(currentValue, suggestedPrompt);
  if (!match) return null;

  return match.suggestion.slice(0, maxLength);
}

/** Cap default / suggested prompt text to the assessment prompt max length. */
export function capAssessmentPrompt(
  prompt: string,
  maxLength: number,
): string {
  return prompt.slice(0, maxLength);
}

export function resolveEffectiveAssessmentPrompt(
  currentValue: string,
  defaultPrompt = "",
  maxLength = 5000,
): string {
  const trimmed = currentValue.trim();
  if (trimmed) return trimmed.slice(0, maxLength);

  const fallback = defaultPrompt.trim();
  return fallback ? fallback.slice(0, maxLength) : "";
}
